import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import apiService from "../../services/apiService";
import { resolveChainLogoCandidates } from "../../utils/chainLogoResolver";

const TYPE_BUCKETS = [
  { id: "evm", label: "EVM", match: (c) => c.chain_type === "evm" && c.slug !== "solana" },
  { id: "cosmos", label: "Cosmos / Tendermint", match: (c) => c.chain_type === "tendermint" || c.chain_type === "tendermint_rest" },
  { id: "solana", label: "Solana", match: (c) => c.slug === "solana" || c.chain_type === "solana" },
  { id: "near", label: "NEAR", match: (c) => c.chain_type === "near" },
];

// Surface-only filters — keep DB rows untouched but hide on the landing.
const SLUG_DENYLIST = new Set([
  // Legacy duplicates of an already-listed family
  "ethereum", "eth-sepolia", "arbitrum-mainnet", "avax-mainnet",
  "bsc-mainnet", "optimism-mainnet", "metis-mainnet", "hyperliquid-core",
  // Testnets / chains we don't operate
  "goerli", "polygon", "mumbai", "blast", "sei", "file-coin",
  "story-mainnet", "shannon-mainnet",
  // REST duplicates (kept under their RPC family by the dedupe below)
  "lava-rest", "noble-rest",
]);

const TYPE_DENYLIST = new Set(["evm_ws", "tendermint_grpc"]);

// Strip "mainnet"/"rest"/"ws" tokens to get a chain "family" key — so
// "Akash Mainnet", "Akash Mainnet REST", "Akash REST" all collapse to one
// tile, and "Metis" / "Metis Mainnet" don't both render.
function chainFamily(chain) {
  return (chain.name || chain.slug || "")
    .toLowerCase()
    .replace(/\bmainnet\b/g, "")
    .replace(/\brest\b/g, "")
    .replace(/\bwebsocket\b/g, "")
    .replace(/\bws\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Pick the cleanest representative of a family: prefer the slug WITHOUT
// `-mainnet`, `-rest`, or `-grpc` suffixes so the tile name reads naturally.
function preferenceScore(c) {
  let s = 0;
  if (c.slug.endsWith("-mainnet")) s += 1;
  if (c.slug.includes("-rest")) s += 2;
  if (c.slug.includes("-grpc")) s += 4;
  if (c.slug.includes("-ws")) s += 4;
  return s;
}

function ChainTile({ chain }) {
  const candidates = useMemo(() => resolveChainLogoCandidates(chain), [chain]);
  const [logoIndex, setLogoIndex] = useState(0);
  const [failed, setFailed] = useState(false);
  const logoUrl = candidates[logoIndex] || null;
  const displayName = (chain.name || chain.slug || "")
    .replace(/\s+Mainnet$/i, "")
    .replace(/\s+REST$/i, "")
    .replace(/\s+WebSocket$/i, "")
    .trim();

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="flex flex-col items-center justify-center gap-3 border border-zinc-700/30 bg-[#1a1a1a] p-5"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0f0f0f] ring-1 ring-zinc-700/40">
        {logoUrl && !failed ? (
          <img
            src={logoUrl}
            alt={displayName}
            className="h-8 w-8 rounded-full object-contain"
            loading="lazy"
            onError={() => {
              const next = logoIndex + 1;
              if (next < candidates.length) {
                setLogoIndex(next);
              } else {
                setFailed(true);
              }
            }}
          />
        ) : (
          <span className="font-secondary text-xs font-semibold uppercase text-white/40">
            {displayName.slice(0, 2) || "—"}
          </span>
        )}
      </div>
      <p className="font-secondary text-center text-sm font-medium leading-tight text-white">
        {displayName}
      </p>
    </motion.div>
  );
}

function ChainTileSkeleton() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 border border-zinc-700/20 bg-[#1a1a1a] p-5">
      <div className="h-12 w-12 animate-pulse rounded-full bg-[#262626]" />
      <div className="h-3 w-20 animate-pulse bg-[#262626]" />
    </div>
  );
}

const SupportedChainsSection = () => {
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getChains();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.results || [];
        setChains(list.filter((c) => c.active));
      } catch {
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleChains = useMemo(() => {
    const cleaned = chains.filter(
      (c) =>
        c.active &&
        !TYPE_DENYLIST.has(c.chain_type) &&
        !SLUG_DENYLIST.has(c.slug),
    );
    // Dedupe by family — keep the entry with the cleanest slug.
    const byFamily = new Map();
    for (const c of cleaned) {
      const family = chainFamily(c);
      const existing = byFamily.get(family);
      if (!existing || preferenceScore(c) < preferenceScore(existing)) {
        byFamily.set(family, c);
      }
    }
    return Array.from(byFamily.values());
  }, [chains]);

  const grouped = useMemo(() => {
    return TYPE_BUCKETS.map((b) => ({
      ...b,
      chains: visibleChains
        .filter(b.match)
        .sort((a, b2) => (a.name || "").localeCompare(b2.name || "")),
    })).filter((b) => b.chains.length > 0);
  }, [visibleChains]);

  if (errored && chains.length === 0) {
    return null;
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="flex w-full flex-col items-start gap-10 px-6 py-20"
    >
      <div className="flex flex-col items-start text-start">
        <p className="font-secondary text-sm uppercase tracking-widest text-primary">
          Supported networks
        </p>
        <h2 className="font-title mt-3 max-w-[720px] text-[36px] font-semibold leading-[1.08] text-white sm:text-[44px] md:text-6xl">
          {loading
            ? "Loading networks…"
            : `${visibleChains.length}+ chains. One API key.`}
        </h2>
      </div>

      {loading ? (
        <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <ChainTileSkeleton key={i} />
          ))}
        </div>
      ) : (
        <div className="flex w-full flex-col gap-10">
          {grouped.map((bucket) => (
            <div key={bucket.id} className="flex w-full flex-col gap-5">
              <div className="flex items-baseline gap-3">
                <h3 className="font-title text-xl font-semibold text-white">
                  {bucket.label}
                </h3>
                <span className="font-secondary text-sm text-white/40">
                  {bucket.chains.length}
                </span>
              </div>
              <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {bucket.chains.map((c) => (
                  <ChainTile key={c.slug} chain={c} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.section>
  );
};

export default SupportedChainsSection;
